// @flow
import { isPlainObject } from "../../utils/helpers/objects";

import { JsonParseException } from "../../exceptions";
import { setSetting, setSettings } from "../../constants/Settings";

import type {
  PreferenceValue,
  ReduxState,
  SetPreferenceAction,
  SetPreferencesAction,
} from "../types";

import {
  ALL_CONTENT_IN_DATA_SETTING,
  INCLUDE_TIME_OFFSET,
  LOGIN_TYPE,
  LOGIN_PASSWORD_SETTING,
  LOGIN_PATH_SETTING,
  LOGIN_USERNAME_SETTING,
  LOGOUT_PATH_SETTING,
} from "../../constants";

/**
 */
const getServerPreferenceValue = (
  preferenceName: string,
  defaultValue: PreferenceValue = null,
  type?: string,
): PreferenceValue => {
  let value: PreferenceValue =
    // $FlowIssue
    globalThis?.preferencesProvider?.getPreferenceByName(preferenceName);

  if (value == null) {
    value = defaultValue;
  }

  if (typeof value === "string" && type !== "string" && value.includes(",")) {
    value = value.split(",").map((val) => val.trim());
  }

  return (value: PreferenceValue);
};

/**
 */
export const setServerPreference = (
  propertyName: string,
  defaultValue: PreferenceValue = null,
  type?: string,
): SetPreferenceAction => {
  const value = getServerPreferenceValue(propertyName, defaultValue, type);
  return setPreference(propertyName, value);
};

/**
 */
export const setPreference = (
  propertyName: string,
  propertyValue: PreferenceValue = null,
): SetPreferenceAction => ({
  type: "SET_PREFERENCE",
  payload: {
    [propertyName]: propertyValue,
  },
});

/**
 */
export const setPreferences = (preferences: Object): SetPreferencesAction => ({
  type: "SET_PREFERENCES",
  payload: {
    ...preferences,
  },
});

/**
 */
export const setThemePreference = (
  configTheme: string,
): SetPreferenceAction => {
  let themeData;

  try {
    themeData = configTheme ? JSON.parse(configTheme) : null;
  } catch (error) {
    throw new JsonParseException(`Theme file contains invalid JSON: ${error}`);
  }

  if (themeData && isPlainObject(themeData)) {
    setSettings(themeData.settings);
  }

  return setPreference("theme", themeData);
};

/**
 * Make modular-ui-content-in-data preference available client side
 */
export const setAllContentInDataSetting = (
  state?: ReduxState,
): SetPreferenceAction => {
  let allContentInDataSetting: PreferenceValue = false;

  if (
    // $FlowIssue
    typeof globalThis.preferencesProvider?.getModularUIContentInData ===
    "function"
  ) {
    allContentInDataSetting =
      // $FlowIssue
      globalThis.preferencesProvider.getModularUIContentInData();
  } else if (typeof state?.preferences == "object") {
    allContentInDataSetting = state.preferences[ALL_CONTENT_IN_DATA_SETTING];
  }

  setSetting(ALL_CONTENT_IN_DATA_SETTING, !!allContentInDataSetting);
  return setPreference(ALL_CONTENT_IN_DATA_SETTING, allContentInDataSetting);
};

/**
 * Retrieve settings for date time conversion
 */
export const setDateTimeSettings = (
  state?: ReduxState,
): SetPreferenceAction => {
  let isIncludeTimeOffsetInDateTimes: PreferenceValue = false;
  if (
    // $FlowIssue
    typeof globalThis.preferencesProvider?.isIncludeTimeOffsetInDateTimes ===
    "function"
  ) {
    isIncludeTimeOffsetInDateTimes =
      // $FlowIssue
      globalThis.preferencesProvider.isIncludeTimeOffsetInDateTimes();
  } else if (typeof state?.preferences === "object") {
    isIncludeTimeOffsetInDateTimes = state.preferences[INCLUDE_TIME_OFFSET];
  }

  setSetting(INCLUDE_TIME_OFFSET, !!isIncludeTimeOffsetInDateTimes);
  return setPreference(INCLUDE_TIME_OFFSET, isIncludeTimeOffsetInDateTimes);
};

/**
 * Retrieves login settings from runtime preferences and sets them as setting in the preference reducer
 */
export const setLoginPreferences = (
  state?: ReduxState,
): SetPreferencesAction => {
  const loginSettings = {
    [LOGIN_TYPE]: getServerPreferenceValue(
      LOGIN_TYPE,
      state?.preferences[LOGIN_TYPE],
    ),
    [LOGIN_PATH_SETTING]: getServerPreferenceValue(
      LOGIN_PATH_SETTING,
      state?.preferences[LOGIN_PATH_SETTING] ??
        `/callback?client_name=FormClient`,
    ),
    [LOGIN_USERNAME_SETTING]: getServerPreferenceValue(
      LOGIN_USERNAME_SETTING,
      state?.preferences[LOGIN_USERNAME_SETTING] ?? "j_username",
    ),
    [LOGIN_PASSWORD_SETTING]: getServerPreferenceValue(
      LOGIN_PASSWORD_SETTING,
      state?.preferences[LOGIN_PASSWORD_SETTING] ?? "j_password",
    ),
    [LOGOUT_PATH_SETTING]: getServerPreferenceValue(
      LOGOUT_PATH_SETTING,
      state?.preferences[LOGOUT_PATH_SETTING] ?? `/logout`,
    ),
  };

  setSettings(loginSettings);
  return setPreferences(loginSettings);
};
